/**
 * Per-shop latch for "the client is deliberately backing out of the standby
 * flow right now". It exists because more than one `useStandbyActiveEntryGuard`
 * instance can be alive during a route transition — expo-router keeps the
 * outgoing screen mounted while the registration screen comes up, and that
 * zombie instance knows nothing about the `returning` param, so a per-screen
 * prop cannot silence it. Any guard instance, current or outgoing, must hold
 * its redirect while this is set for its shop, or it races the
 * exit-confirmation UI off the screen.
 *
 * Scoped by shopId (already threaded through every guard call) rather than
 * process-wide: the zombie instances it's meant to silence are always
 * same-shop, and a global latch would also hold a guard for a *different*
 * shop where the customer separately has an active entry — e.g. back out of
 * shop A's entry, then within the TTL open shop B's funnel, where B's guard
 * should redirect immediately, not wait out shop A's exit.
 *
 * TTL-bounded so an abandoned exit gesture (navigated elsewhere before
 * answering the confirmation) cannot permanently disable the guard; the
 * join-time duplicate redirect remains the backstop either way.
 */
const EXIT_INTENT_TTL_MS = 15_000;

const exitIntentAtByShop = new Map<string, number>();

export const beginStandbyExit = (shopId: string): void => {
  exitIntentAtByShop.set(shopId, Date.now());
};

export const endStandbyExit = (shopId: string): void => {
  exitIntentAtByShop.delete(shopId);
};

export const isStandbyExitActive = (shopId: string): boolean => {
  const startedAt = exitIntentAtByShop.get(shopId);
  if (startedAt === undefined) return false;

  if (Date.now() - startedAt >= EXIT_INTENT_TTL_MS) {
    // Prune here rather than adding a separate sweep: this is the natural
    // observation point (every guard check passes through it), and it's the
    // only way an abandoned exit gesture's entry ever gets removed — nothing
    // else calls endStandbyExit for a shop the client never answered.
    exitIntentAtByShop.delete(shopId);
    return false;
  }

  return true;
};

/**
 * Ms remaining until the latch clears on its own for this shop (0 if already
 * clear). A guard instance that observes the latch mid-effect has no other
 * signal telling it when that happens — it isn't a dependency, and nothing
 * else about the guard's inputs necessarily changes when the TTL lapses — so
 * a caller needs this to schedule its own re-check rather than staying
 * suppressed indefinitely.
 */
export const standbyExitClearsInMs = (shopId: string): number => {
  const startedAt = exitIntentAtByShop.get(shopId);
  return startedAt === undefined
    ? 0
    : Math.max(0, EXIT_INTENT_TTL_MS - (Date.now() - startedAt));
};
